// src/portals/creator/pages/MonetizationHubPage.js
import React, { useState, useEffect } from "react";
import Tabs from "../../../components/ui/Tabs";
import { useData } from "../../../context/DataContext";
import {
  mockCreator,
  platformAffiliateDeals as initialDeals,
} from "../../../data/mockData";
import RevenueGoalWidget from "../components/RevenueGoalWidget";

// Import all tabs
import SubscriptionsTab from "../components/tabs/SubscriptionsTab";
import CommerceTab from "../components/tabs/CommerceTab";
import EventsTab from "../components/tabs/EventsTab";
import ExperiencesTab from "../components/tabs/ExperiencesTab";
import AdCampaignsTab from "../components/tabs/AdCampaignsTab";
import DonationCampaignsTab from "../components/tabs/DonationCampaignsTab";
import MasterClassTab from "../components/tabs/MasterClassTab";
// import CrowdfundingTab from "../components/tabs/CrowdfundingTab";
import AffiliatesTab from "../components/tabs/AffiliatesTab";

// Import all modals
import ManageSubscriptionsModal from "../components/modals/ManageSubscriptionsModal";
import CreateProductModal from "../components/modals/CreateProductModal";
import CreateEventModal from "../components/modals/CreateEventModal";
import CreateExperienceModal from "../components/modals/CreateExperienceModal";
import CreateCampaignModal from "../components/modals/CreateCampaignModal";
import CreateDonationCampaignModal from "../components/modals/CreateDonationCampaignModal";
import CreateCourseModal from "../components/modals/CreateCourseModal";
// import CreateCrowdfundingModal from "../components/modals/CreateCrowdfundingModal";

const MonetizationHubPage = () => {
    // Sync with global data
  const { adCampaigns: globalAdCampaigns, affiliates: globalAffiliates, donations: globalDonations } = useData();

  // State for all data lists
  const [subscriptions, setSubscriptions] = useState(
    mockCreator.subscriptionProducts
  );
  const [products, setProducts] = useState(mockCreator.merchandise);
  const [events, setEvents] = useState(mockCreator.events);
  const [experiences, setExperiences] = useState(mockCreator.experiences);
  
  // Use global data for these opportunities
  const adCampaigns = globalAdCampaigns || [];
  const donationCampaigns = globalDonations || [];
  
  const [courses, setCourses] = useState(mockCreator.courses);
  const [projects, setProjects] = useState(mockCreator.crowdfundingProjects);

  const [affiliateDeals, setAffiliateDeals] = useState(globalAffiliates);
  
  const [totalRevenue, setTotalRevenue] = useState(0); // Initialize as number for calculation
  const [supportedCauseIds, setSupportedCauseIds] = useState(
    mockCreator.supportedCauseIds
  );

  // Effect to simulate recalculating total revenue
  useEffect(() => {
    const productRevenue = products.reduce(
      (acc, p) => acc + p.priceETB * p.sales,
      0
    );
    const eventRevenue = events.reduce(
      (acc, e) => acc + (e.priceETB || 0) * e.ticketsSold,
      0
    );
    // Rough calc of mock content revenue for demo
    const estimatedTotal = productRevenue + eventRevenue + 12500; // + base
    setTotalRevenue(estimatedTotal);
  }, [products, events]);

  const [activeModal, setActiveModal] = useState({ type: null, data: null });

  const handleSave = (type, data) => {
    const updaters = {
      subscription: setSubscriptions,
      product: setProducts,
      event: setEvents,
      experience: setExperiences,
      // campaign: setAdCampaigns, // We don't update global campaigns from here yet
      // donation: setDonationCampaigns, // Same
      course: setCourses,
      crowdfunding: setProjects,
    };
    const lists = {
      subscription: subscriptions,
      product: products,
      event: events,
      experience: experiences,
      // campaign: adCampaigns,
      // donation: donationCampaigns,
      course: courses,
      crowdfunding: projects,
    };

    const list = lists[type];
    const updater = updaters[type];
    if (!list || !updater) return; // Safety check

    const isEditing = list.some((item) => item.id === data.id);

    if (isEditing) {
      updater(list.map((item) => (item.id === data.id ? data : item)));
    } else {
      updater([data, ...list]);
    }
  };

  const handleActivateDeal = (dealId) => {
    setAffiliateDeals(
      affiliateDeals.map((deal) =>
        deal.id === dealId ? { ...deal, isActivated: true } : deal
      )
    );
  };

  const openModal = (type, data = null) => setActiveModal({ type, data });
  const closeModal = () => setActiveModal({ type: null, data: null });

  const handleToggleSupport = (causeId) => {
    setSupportedCauseIds((prevIds) => {
      if (prevIds.includes(causeId)) {
        return prevIds.filter((id) => id !== causeId); // Remove if it exists
      } else {
        return [...prevIds, causeId]; // Add if it doesn't exist
      }
    });
  };

  const tabs = [
    {
      name: "Subscriptions",
      content: (
        <SubscriptionsTab
          subscriptions={subscriptions}
          onCreate={() => openModal("subscription")}
          onEdit={(sub) => openModal("subscription", sub)}
        />
      ),
    },
    {
      name: "Experiences",
      content: (
        <ExperiencesTab
          experiences={experiences}
          // ✨ Pass correct props ✨
          onCreate={() => openModal("experience")}
          onEdit={(exp) => openModal("experience", exp)}
        />
      ),
    },
    {
      name: "Commerce",
      content: (
        <CommerceTab
          products={products}
          onCreate={() => openModal("product")}
          onEdit={(prod) => openModal("product", prod)}
        />
      ),
    },
    {
      name: "Events",
      content: (
        <EventsTab
          events={events}
          onCreate={() => openModal("event")}
          onEdit={(evt) => openModal("event", evt)}
        />
      ),
    },
    {
      name: "Ad Campaigns",
      content: (
        <AdCampaignsTab
          campaigns={adCampaigns}
          onCreate={() => openModal("campaign")}
          onEdit={(camp) => openModal("campaign", camp)}
        />
      ),
    },
    {
      name: "Affiliates & Referrals",
      content: (
        <AffiliatesTab
          deals={affiliateDeals}
          onActivateDeal={handleActivateDeal}
        />
      ),
    },
    {
      name: "MasterClass",
      content: (
        <MasterClassTab
          courses={courses}
          onCreate={() => openModal("course")}
        />
      ),
    },
    {
      name: "Donations",
      content: (
        <DonationCampaignsTab
          campaigns={donationCampaigns}
          onCreate={() => openModal("donation")}
          // ✨ Pass the new state and handler down
          supportedCauseIds={supportedCauseIds}
          onToggleSupport={handleToggleSupport}
        />
      ),
    },
  ];

  return (
    <>
      <div>
        <div className="mb-8">
          <div className="flex flex-col @md:flex-row items-start @md:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl @sm:text-3xl font-bold text-slate-800">
                Monetization Hub
              </h1>
              <p className="text-slate-500 mt-1 text-sm">
                Manage all your revenue streams in one place.
              </p>
            </div>
          </div>
          
          <RevenueGoalWidget currentRevenue={totalRevenue} />

          <div className="grid grid-cols-1 @sm:grid-cols-3 gap-4 mb-8">
             <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
                <p className="text-xs text-slate-500 uppercase tracking-tight font-medium">Calculated Revenue</p>
                <p className="text-xl @sm:text-2xl font-bold text-slate-800">{totalRevenue.toLocaleString()} ETB</p>
             </div>
             <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
                <p className="text-xs text-slate-500 uppercase tracking-tight font-medium">Pending Payouts</p>
                <p className="text-xl @sm:text-2xl font-bold text-slate-800">0 ETB</p>
             </div>
             <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
                 <p className="text-xs text-slate-500 uppercase tracking-tight font-medium">Active Streams</p>
                 <p className="text-xl @sm:text-2xl font-bold text-slate-800">4</p>
             </div>
          </div>

        </div>
        <Tabs tabs={tabs} />
      </div>

      {/* ✨ FIX: Ensured all modals receive the correct onSave prop ✨ */}
      <ManageSubscriptionsModal
        isOpen={activeModal.type === "subscription"}
        onClose={closeModal}
        onSave={(data) => handleSave("subscription", data)}
        subscriptions={subscriptions}
        editingSubscription={activeModal.data}
      />
      <CreateProductModal
        isOpen={activeModal.type === "product"}
        onClose={closeModal}
        onSave={(data) => handleSave("product", data)}
        productToEdit={activeModal.data}
      />
      <CreateEventModal
        isOpen={activeModal.type === "event"}
        onClose={closeModal}
        onSave={(data) => handleSave("event", data)}
        eventToEdit={activeModal.data}
      />
      <CreateExperienceModal
        isOpen={activeModal.type === "experience"}
        onClose={closeModal}
        onSave={(data) => handleSave("experience", data)}
        experienceToEdit={activeModal.data}
      />
      <CreateCampaignModal
        isOpen={activeModal.type === "campaign"}
        onClose={closeModal}
        onSave={(data) => handleSave("campaign", data)}
        campaignToEdit={activeModal.data}
      />
      <CreateDonationCampaignModal
        isOpen={activeModal.type === "donation"}
        onClose={closeModal}
        onSave={(data) => handleSave("donation", data)}
      />
      <CreateCourseModal
        isOpen={activeModal.type === "course"}
        onClose={closeModal}
        onSave={(data) => handleSave("course", data)}
      />
      {/* <CreateCrowdfundingModal
        isOpen={activeModal.type === "crowdfunding"}
        onClose={closeModal}
        onSave={(data) => handleSave("crowdfunding", data)}
      /> */}
    </>
  );
};

export default MonetizationHubPage;
